import { useMemo } from 'react';
import {
  AudienceFilterMappingError,
  mapConditionNodeToFilterDto,
} from '@/components/audiences/map-tree-to-filter-dto';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { useAudiencePreview } from '@/hooks/use-audiences';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import type { FilterNodeDto } from '@/generated/korza-api-client/types.gen';

const PREVIEW_DEBOUNCE_MS = 500;

function tryMapCondition(node: TreeConditionNode): FilterNodeDto | undefined {
  try {
    return mapConditionNodeToFilterDto(node);
  } catch (error) {
    if (error instanceof AudienceFilterMappingError) return undefined;
    throw error;
  }
}

/** Preview audience size for a single condition (debounced). */
export function useConditionAudiencePreview(
  node: TreeConditionNode | undefined,
  enabled: boolean
) {
  const stableKey = node
    ? JSON.stringify({
        kind: node.kind,
        negated: node.negated,
        args: node.args,
      })
    : null;

  const filters = useMemo((): FilterNodeDto | undefined => {
    if (!node || stableKey === null) return undefined;
    return tryMapCondition(node);
    // Intentionally keyed by serialized condition content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);

  const debouncedFilters = useDebouncedValue(filters, PREVIEW_DEBOUNCE_MS);
  const isDebouncing = filters !== debouncedFilters;

  const previewQuery = useAudiencePreview(
    enabled && debouncedFilters ? debouncedFilters : undefined
  );

  return {
    targetedUserCount: previewQuery.data?.targetedUserCount,
    totalUserCount: previewQuery.data?.notificationsEnabledUserCount,
    isLoading:
      enabled &&
      Boolean(debouncedFilters) &&
      (isDebouncing || previewQuery.isFetching || previewQuery.isPending),
    isError: previewQuery.isError,
  };
}
